<template>
  <v-container fluid class="pa-4">
    <v-breadcrumbs :items="breadcrumbs" class="px-0" />

    <div v-if="loading" class="pa-8 text-center"><v-progress-circular indeterminate /></div>
    <div v-else-if="!section" class="pa-8 text-center"><v-alert type="error">Sección no encontrada</v-alert></div>
    <div v-else>
      <div class="d-flex align-center mb-4">
        <h2 class="text-h5 font-weight-bold">{{ section.name }}</h2>
        <v-spacer />
        <v-btn color="primary" @click="openCreateQuestion">
          <v-icon left>mdi-plus</v-icon> Nueva pregunta
        </v-btn>
      </div>

      <v-card>
        <v-data-table
            :headers="headers"
            :items="section.questions || []"
            :items-per-page="20"
        >
            <template #[`item.text`]="{ item }">
                <div class="font-weight-medium">{{ item.code }}</div>
                <div class="text-caption grey--text">{{ item.text }}</div>
            </template>
            <template #[`item.weight`]="{ item }">
                <v-chip x-small :color="item.weightMode === 'ABSOLUTE' ? 'orange' : 'primary'" text-color="white">
                    {{ weightModeLabel(item.weightMode) }}
                </v-chip>
                <span class="ml-2">{{ item.weight }}</span>
            </template>
            <template #[`item.optionsCount`]="{ item }">
                {{ (item.options || []).length }}
            </template>
            <template #[`item.flags`]="{ item }">
                <v-chip v-if="item.isProfile" x-small color="grey" text-color="white" class="mr-1">Perfil</v-chip>
                <v-chip v-if="item.isRequired" x-small color="error" text-color="white" class="mr-1">Requerida</v-chip>
                <v-chip x-small>{{ fieldTypeLabel(item.fieldType) }}</v-chip>
            </template>
            <template #[`item.actions`]="{ item }">
                <v-btn icon small :to="`/sac/admin/config/question/${item._id}`">
                    <v-icon>mdi-pencil</v-icon>
                </v-btn>
            </template>
        </v-data-table>
      </v-card>
    </div>

    <v-dialog v-model="dialog" max-width="600">
      <v-card>
        <v-card-title>Crear pregunta</v-card-title>
        <v-card-text>
          <v-text-field v-model="form.text" label="Texto" outlined dense />
          <v-select
              v-model="form.fieldType"
              :items="fieldTypeOptions"
              item-text="text"
              item-value="value"
              label="Tipo de campo"
              outlined dense
              class="mt-2"
          />
          <v-text-field
              v-model.number="form.weight"
              type="number"
              :label="form.weightMode === 'ABSOLUTE' ? 'Peso (puntos directos, máx. 100)' : 'Peso (proporcional, se reparte la sección)'"
              :hint="form.weightMode === 'ABSOLUTE' ? 'Suma del peso de toda la sección. Ej: 25 significa hasta 25 puntos directos.'
                  : 'Proporción dentro de la sección. La suma de todas las preguntas relativas de la sección debe sumar 1 (o 100 si usas porcentajes).'"
              outlined dense
              persistent-hint
              class="mt-2"
          />
          <div class="weight-mode-toggle mt-3">
            <button
              type="button"
              class="wm-btn"
              :class="{ active: form.weightMode === 'RELATIVE' }"
              @click="form.weightMode = 'RELATIVE'"
            >
              <div class="wm-title">Relativo</div>
              <div class="wm-desc">El peso se reparte proporcionalmente dentro de la sección.</div>
            </button>
            <button
              type="button"
              class="wm-btn"
              :class="{ active: form.weightMode === 'ABSOLUTE' }"
              @click="form.weightMode = 'ABSOLUTE'"
            >
              <div class="wm-title">Absoluto</div>
              <div class="wm-desc">El peso se suma como puntos directos (total de la sección = 100).</div>
            </button>
          </div>
          <v-text-field v-model="form.code" label="Código (opcional, se autogenera)" outlined dense class="mt-2" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="saveQuestion">Crear</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-alert type="error" v-if="error" class="mt-3" dismissible @input="error = null">{{ error }}</v-alert>
  </v-container>
</template>

<script>
import { mapMutations } from 'vuex';
import { listRubros, createQuestion } from '../../api/sac';

export default {
    name: 'SacConfigSection',
    data() {
        return {
            loading: true,
            section: null,
            dialog: false,
            saving: false,
            error: null,
            form: { text: '', fieldType: 'YES_NO', weight: 0, weightMode: 'RELATIVE', code: '' },
            fieldTypeOptions: [
                { value: 'YES_NO', text: 'Sí / No' },
                { value: 'LIKERT', text: 'Escala Likert' },
                { value: 'MULTIPLE', text: 'Opciones múltiples' },
                { value: 'SCALE', text: 'Escala 0-100' },
                { value: 'NUMBER', text: 'Numérico' },
                { value: 'DATE', text: 'Fecha' },
                { value: 'TEXT', text: 'Texto libre' },
            ],
            weightModeOptions: [
                { value: 'RELATIVE', text: 'Relativo (proporcional al rubro)' },
                { value: 'ABSOLUTE', text: 'Absoluto (valor fijo)' },
            ],
            headers: [
                { text: 'Pregunta', value: 'text' },
                { text: 'Peso', value: 'weight' },
                { text: 'Opciones', value: 'optionsCount' },
                { text: 'Etiquetas', value: 'flags', sortable: false },
                { text: '', value: 'actions', sortable: false, width: 60 },
            ],
        };
    },
    computed: {
        breadcrumbs() {
            const r = this.section?.rubro;
            return [
                { text: 'SAC Config', to: '/sac/admin/config', exact: true },
                ...(r ? [{ text: r.name, to: `/sac/admin/config/rubro/${r._id}` }] : []),
                { text: this.section?.name || '...' },
            ];
        },
    },
    mounted() {
        this.load();
    },
    methods: {
        ...mapMutations('notification', ['show']),
        fieldTypeLabel(v) {
            const o = this.fieldTypeOptions.find((x) => x.value === v);
            return o ? o.text : v;
        },
        weightModeLabel(v) {
            return v === 'ABSOLUTE' ? 'Absoluto' : 'Relativo';
        },
        async load() {
            this.loading = true;
            try {
                const res = await listRubros();
                for (const r of res.rubros || []) {
                    const sec = (r.sections || []).find((s) => s._id === this.$route.params.id);
                    if (sec) {
                        sec.rubro = r;
                        this.section = sec;
                        break;
                    }
                }
            } catch (e) {
                this.error = e?.response?.data?.error?.message || e?.response?.data?.message || e.message || 'No se pudo cargar la sección';
                this.show({ text: this.error, color: 'error' });
            } finally {
                this.loading = false;
            }
        },
        openCreateQuestion() {
            this.form = { text: '', fieldType: 'YES_NO', weight: 0, weightMode: 'RELATIVE', code: '' };
            this.dialog = true;
        },
        async saveQuestion() {
            this.saving = true;
            try {
                await createQuestion({ ...this.form, section: this.section._id });
                this.dialog = false;
                await this.load();
                this.show({ text: 'Pregunta creada correctamente', color: 'success' });
            } catch (e) {
                this.error = e?.response?.data?.error?.message || e?.response?.data?.message || e.message || 'No se pudo crear la pregunta';
                this.show({ text: this.error, color: 'error' });
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.weight-mode-toggle {
  display: flex;
  gap: 8px;
}
.wm-btn {
  flex: 1;
  text-align: left;
  background: #fff;
  border: 1px solid #c4c4c4;
  border-radius: 6px;
  padding: 10px 12px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  font: inherit;
  color: #333;
}
.wm-btn:hover {
  border-color: #1976d2;
}
.wm-btn.active {
  border-color: #1976d2;
  background: #e3f2fd;
  box-shadow: 0 0 0 1px #1976d2 inset;
}
.wm-title {
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 2px;
  color: #1976d2;
}
.wm-desc {
  font-size: 0.78rem;
  color: #555;
  line-height: 1.3;
}
</style>
